/**
 * Delegates - HR names who does a manager's work while they're out (or,
 * for the CEO, permanently). One person for everything or one per area.
 *
 *   · Tasks, approvals, reminders and emails go to the delegate instead
 *     of the manager. The manager keeps full read access and can still
 *     act; the audit records both names (actorId + onBehalfOf).
 *   · Not transitive: a delegate's own delegate is never followed.
 *   · Only HR sets it (People → Edit → Delegates). Managers and above only.
 *   · Areas offered = what the person can actually have: managers get
 *     jd / reviews / ta / hiring; comp_exec only for the CEO's direct
 *     reports; comp_ceo only for the CEO.
 *
 * Resolution is done once per sign-in in the pre-token Lambda from the
 * DELEGATIONS index row and carried as the `custom:covers` claim, so
 * every handler can answer "does the caller act for X in area Y?"
 * synchronously (UserContext.covers). Changes take effect on the
 * delegate's next token refresh (≤ 1h) or sign-in.
 */

export type DelegateArea = 'jd' | 'reviews' | 'ta' | 'hiring' | 'comp_exec' | 'comp_ceo'
export const DELEGATE_AREAS: DelegateArea[] = ['jd', 'reviews', 'ta', 'hiring', 'comp_exec', 'comp_ceo']

export const DELEGATE_AREA_META: Record<DelegateArea, { label: string; short: string; desc: string; who: 'managers' | 'executives' | 'ceo' }> = {
  jd:        { label: 'Job descriptions', short: 'JDs', who: 'managers', desc: "Approve JD changes submitted by their reports · confirm or update their reports' JDs in a JD update cycle · \"My team's JDs\"" },
  reviews:   { label: 'Performance reviews', short: 'Reviews', who: 'managers', desc: 'Write and submit manager reviews for their reports · acknowledge calibration · release reviews after the 1:1' },
  ta:        { label: 'Time off', short: 'Time off', who: 'managers', desc: 'Approve or deny PTO / sick / personal requests from their reports' },
  hiring:    { label: 'Hiring', short: 'Hiring', who: 'managers', desc: 'Applicant review and stage decisions on openings they own' },
  comp_exec: { label: 'Compensation · executive', short: 'Comp (team)', who: 'executives', desc: "Allocate and submit their team's pool in a comp cycle" },
  comp_ceo:  { label: 'Compensation · CEO approvals', short: 'Comp (CEO)', who: 'ceo', desc: 'Approve or send back executive teams in a comp cycle' },
}

export interface DelegateRef { empId: string }

/** Stored on Employee.delegation. */
export interface Delegation {
  /** One person for everything (areas not in byArea fall back to this). */
  all: DelegateRef | null
  /** Per-area overrides. */
  byArea: Partial<Record<DelegateArea, DelegateRef>>
  /** YYYY-MM-DD, inclusive. Null = open-ended. */
  from: string | null
  to: string | null
  note: string | null
  setBy: string
  setAt: string
}

/** Index row (PK DELEGATIONS / SK INDEX) - principal empId → delegation. Read by pre-token. */
export type DelegationIndex = Record<string, Delegation>

/** What the caller covers for others: area → principal empIds. On UserContext. */
export type Covers = Partial<Record<DelegateArea, string[]>>

/** For the SPA (/me): who I'm covering, and who covers me. */
export interface DelegatingFor {
  forId: string
  displayName: string
  areas: DelegateArea[]
  until: string | null
}
export interface CoveredBy {
  area: DelegateArea
  empId: string
  displayName: string
}

export function delegationActive(d: Delegation | null | undefined, today: string): d is Delegation {
  if (!d) return false
  if (d.from && today < d.from) return false
  if (d.to && today > d.to) return false
  return true
}

/** Resolve the delegate for one area (null = nobody). Ignores dates - check delegationActive first. */
export function delegateFor(d: Delegation | null | undefined, area: DelegateArea): string | null {
  if (!d) return null
  return d.byArea?.[area]?.empId ?? d.all?.empId ?? null
}

/** Every area the delegation names, resolved. */
export function delegatedAreas(d: Delegation | null | undefined, areas: DelegateArea[] = DELEGATE_AREAS): Array<{ area: DelegateArea; empId: string }> {
  if (!d) return []
  return areas.map(area => ({ area, empId: delegateFor(d, area) })).filter((x): x is { area: DelegateArea; empId: string } => !!x.empId)
}

/** Build the `covers` map for `empId` from the index (date-aware). */
export function coversFor(index: DelegationIndex, empId: string, today: string): Covers {
  const out: Covers = {}
  for (const [principal, d] of Object.entries(index)) {
    if (principal === empId || !delegationActive(d, today)) continue
    for (const { area, empId: delegate } of delegatedAreas(d)) {
      if (delegate !== empId) continue
      ;(out[area] ||= []).push(principal)
    }
  }
  return out
}

/** Compact wire form for the JWT claim: "jd:a,b;ta:a". */
export function encodeCovers(c: Covers): string {
  // Deterministic (sorted) so two encodings of the same coverage compare equal.
  return Object.entries(c).filter(([, ids]) => ids && ids.length).sort(([a], [b]) => a.localeCompare(b))
    .map(([a, ids]) => `${a}:${[...new Set(ids!)].sort().join(',')}`).join(';')
}
export function decodeCovers(s: string | undefined | null): Covers {
  const out: Covers = {}
  if (!s) return out
  for (const part of s.split(';')) {
    const [area, ids] = part.split(':')
    if (!area || !ids || !(DELEGATE_AREAS as string[]).includes(area)) continue
    out[area as DelegateArea] = ids.split(',').filter(Boolean)
  }
  return out
}
